import { useState, useEffect } from 'react'
import Layout from '../components/LayoutNext'
import LayoutContainer from '../components/LayoutNext/LayoutContainer'
import gearBg2 from '@/retail/components/svgs/gear_bg_2.svg'

const ROICalculator = () => {
  // State for input values
  const [monthlyPartsSpend, setMonthlyPartsSpend] = useState(40000)
  const [currentLeadTime, setCurrentLeadTime] = useState(6)
  const [mechanicHourlyWage, setMechanicHourlyWage] = useState(35)
  const [mechanicsCount, setMechanicsCount] = useState(9)

  // Calculated values
  const [potentialSavingsLow, setPotentialSavingsLow] = useState(24000)
  const [potentialSavingsHigh, setPotentialSavingsHigh] = useState(72000)
  const [potentialLeadTime, setPotentialLeadTime] = useState(1.2)
  const [recoveredEfficiency, setRecoveredEfficiency] = useState(1680)
  const [totalAnnualSavingsLow, setTotalAnnualSavingsLow] = useState(205440)
  const [totalAnnualSavingsHigh, setTotalAnnualSavingsHigh] = useState(253440)

  // Calculate values when inputs change
  useEffect(() => {
    // Calculate potential annual savings (5-15% of annual parts spend)
    const annualPartsSpend = monthlyPartsSpend * 12
    const lowSavings = Math.round(annualPartsSpend * 0.05)
    const highSavings = Math.round(annualPartsSpend * 0.15)
    setPotentialSavingsLow(lowSavings)
    setPotentialSavingsHigh(highSavings)

    // Calculate potential lead time (assuming 5x faster)
    const newLeadTime = Math.round((currentLeadTime / 5) * 100) / 100
    setPotentialLeadTime(newLeadTime)

    // Calculate recovered efficiency (12 hrs/week per mechanic * hourly wage * 4 weeks)
    const monthlyEfficiency = Math.round(12 * mechanicHourlyWage * 4)
    setRecoveredEfficiency(monthlyEfficiency)

    // Calculate total annual savings
    // Parts savings + (monthly efficiency * mechanics count * 12 months)
    const efficiencySavings = monthlyEfficiency * mechanicsCount * 12
    setTotalAnnualSavingsLow(lowSavings + efficiencySavings)
    setTotalAnnualSavingsHigh(highSavings + efficiencySavings)
  }, [monthlyPartsSpend, currentLeadTime, mechanicHourlyWage, mechanicsCount])

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <Layout metaTitle="ROI Calculator">
      <LayoutContainer
        className="roi-calculator-top-bg"
        style={{
          backgroundImage: `url(${gearBg2})`,
        }}
      >
        <div className="max-w-6xl mx-auto my-10 px-5 font-sans ">
          <h1 className="text-5xl font-medium text-center mb-4">Gearflow ROI Calculator</h1>
          <p className="text-lg text-center mb-10 text-gray-600 max-w-2xl mx-auto">
            Calculate how much your team could save by using Gearflow for parts procurement
          </p>

          <div className="flex flex-col md:flex-row gap-10 bg-white rounded-xl shadow-lg p-10 mb-10">
            <div className="flex-1 flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-base font-medium text-gray-600">
                  Your Monthly Parts Spend
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-600 font-medium">$</span>
                  <input
                    type="number"
                    className="w-full py-3 px-3 pl-8 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value={monthlyPartsSpend}
                    onChange={(e) => setMonthlyPartsSpend(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-base font-medium text-gray-600">
                  Your Current Lead Times on Parts (days)
                </label>
                <input
                  type="number"
                  className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={currentLeadTime}
                  onChange={(e) => setCurrentLeadTime(Number(e.target.value))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-base font-medium text-gray-600">Mechanic Hourly Wage</label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-600 font-medium">$</span>
                  <input
                    type="number"
                    className="w-full py-3 px-3 pl-8 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value={mechanicHourlyWage}
                    onChange={(e) => setMechanicHourlyWage(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-base font-medium text-gray-600">How Many Mechanics?</label>
                <input
                  type="number"
                  className="w-full py-3 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={mechanicsCount}
                  onChange={(e) => setMechanicsCount(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="flex-1  ">
              <div className="bg-gray-50 rounded-lg p-6 flex flex-col gap-6">
                <div className="flex flex-col gap-2 pb-4 border-b border-gray-200">
                  <h3 className="text-base font-medium text-gray-600 m-0">
                    Potential Annual Savings Range
                  </h3>
                  <div className="text-2xl font-bold text-orange-500">
                    {formatCurrency(potentialSavingsLow)} - {formatCurrency(potentialSavingsHigh)}
                  </div>
                  <p className="text-sm text-gray-600 m-0">
                    Teams using Gearflow save 5% - 15% across their annual parts spend
                  </p>
                  <a
                    href="https://blog.gearflow.com/ajax-case-study"
                    target="_blank"
                    className="text-sm text-orange-500 cursor-pointer underline mt-1"
                  >
                    Ajax paving saves $30k/mo on parts
                  </a>
                </div>

                <div className="flex flex-col gap-2 pb-4 border-b border-gray-200">
                  <h3 className="text-base font-medium text-gray-600 m-0">
                    Potential Speed of Getting Parts (days)
                  </h3>
                  <div className="text-2xl font-bold text-orange-500">{potentialLeadTime}</div>
                  <p className="text-sm text-gray-600 m-0">
                    Teams using Gearflow are getting parts up to 7x faster
                  </p>
                  <a
                    href="https://blog.gearflow.com/how-integration-took-parts-ordering-from-chaos-to-1-click"
                    target="_blank"
                    className="text-sm cursor-pointer underline text-orange-500 mt-1"
                  >
                    R.C. Construction went from 7-8 day lead times to 1-2 days
                  </a>
                </div>

                <div className="flex flex-col gap-2">
                  <h3 className="text-base font-medium text-gray-600 m-0">
                    Potentially Recovered Efficiency per Mechanic per Month
                  </h3>
                  <div className="text-2xl font-bold text-orange-500">
                    {formatCurrency(recoveredEfficiency)}
                  </div>
                  <p className="text-sm text-gray-600 m-0">
                    Teams using Gearflow are saving 12 hrs/week per shop employee
                  </p>
                  <a
                    href="https://www.mossutilities.com/"
                    target="_blank"
                    className="text-sm text-orange-500 cursor-pointer underline mt-1"
                  >
                    Moss Utilities in Dallas saves 12 hrs/week per in busywork
                  </a>
                </div>
              </div>

              <div className="mt-4 bg-orange-50 rounded-lg overflow-hidden">
                <div className="border-l-4 border-orange-500 p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Annual Available Savings on Parts + Team Efficiency
                  </h3>
                  <div className="text-3xl font-bold text-orange-500">
                    {formatCurrency(totalAnnualSavingsLow)} -{' '}
                    {formatCurrency(totalAnnualSavingsHigh)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <a
              href="/signup/parts-hub"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold text-lg py-4 px-8 rounded-lg transition-colors duration-200"
            >
              Get Started with Gearflow
            </a>
            <p className="text-base text-gray-600 mt-4">
              Join the teams already saving time and money with Gearflow
            </p>
          </div>
        </div>
      </LayoutContainer>
    </Layout>
  )
}

export default ROICalculator
